import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DomainDisabledOutlinedIcon from "@mui/icons-material/DomainDisabledOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { useSelector } from "react-redux";
import Backdrop from "../components/Backdrop";
import DynamicDataGrid from "../components/dynamicDataGrid";
import AddCompany from "./addCompany";

const CompanyLists = () => {
  const [showLoader, setShowLoader] = useState(true);
  const [companyListRows, setCompanyListRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [searchParams, setSearchParams] = useSearchParams();
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);

  const [sortModel, setSortModel] = useState([]);

  const [paginationModel, setPaginationModel] = useState({
    page: Number(searchParams.get("page")) - 1 || 0,
    pageSize: 10,
  });

  const navigate = useNavigate();

  const HandleRowClick = (row) => {
    const currentUrl = window.location.href;
    sessionStorage.setItem("previousUrl", currentUrl);
    navigate(`/CompanyDetails/${row.id}?tab=companyInfo`);
    window.scrollTo(0, 0);
  };

  // Sort change hone par page 1 pe reset karo aur API call trigger karo
  const handleSortModelChange = React.useCallback((newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", 1);
      return params;
    });
  }, []);

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    // setPaginationModel((prev) => ({ ...prev, page: 0 }));
    // setSearchParams({ page: 1, search: query });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", model.page + 1);
      params.set("search", debouncedSearch);
      return params;
    });
  };

  const sortFieldMap = {
    name: "name",
    city: "city",
    state: "state",
    date_created: "date_created",
    phone1: "phone1",
    branch_count: "branch_count",
  };

  const listofComapniesApi = async () => {
    setShowLoader(true);
    setCompanyListRows([]);
    const page = searchParams.get("page") || 1;
    const searchQueryParam = searchParams.get("search") || "";
    let apiUrl = `/list-of-companies/${page}`;
    if (searchQueryParam)
      apiUrl += `?search=${encodeURIComponent(searchQueryParam)}`;

    const payload = { pageSize: paginationModel.pageSize || 10 };
    if (sortModel?.length > 0) {
      payload.sortField = sortFieldMap[sortModel[0].field] || "date_created";
      payload.sortBy = sortModel[0].sort;
    }
    console.log("SORT MODEL:", sortModel);
    console.log("SORT FIELD SENT:", payload.sortField);
    console.log("SORT ORDER SENT:", payload.sortBy);

    try {
      const response = await axios.post(apiUrl, payload, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      setCompanyListRows(response.data.message.companyListResponse);
      setTotalCount(response.data.message?.total);
    } catch (error) {
      console.error(error);
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", 1);
      params.set("search", debouncedSearch);
      return params;
    });
  }, [debouncedSearch]);

  useEffect(() => {
    listofComapniesApi();
  }, [searchParams, sortModel, paginationModel.pageSize]);

  const managePermissions = useSelector((state) => state.user.permissions);
  const AddCompanyPermissions = managePermissions["200"];

  return (
    <Box className="pageWrapper">
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{ fontSize: "18px", fontWeight: 600, color: "#151D48" }}
        >
          Companies
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <TextField
            id="standard-search"
            type="search"
            variant="outlined"
            placeholder="Search"
            autoComplete="off"
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "18px", color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "240px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13px",
                backgroundColor: "#F8FAFC",
                height: "32px",
              },
            }}
          />

          {AddCompanyPermissions?.add === 1 && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => setAddCompanyOpen(true)}
              variant="contained"
              sx={{
                backgroundColor: "#3F95EA",
                borderRadius: "8px",
                textTransform: "uppercase",
                fontWeight: 400,
                fontSize: "14px",
                px: 2,
                height: "32px",
                boxShadow: "none",
                letterSpacing: "0.5px",
                "&:hover": {
                  backgroundColor: "#2e82d8",
                  boxShadow: "none",
                },
              }}
            >
              Add Company
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Table ── */}
      <Box
        sx={{
          height: "100%",
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DynamicDataGrid
          rows={companyListRows}
          loading={showLoader}
          showCheckbox={false}
          onRowSelection={HandleRowClick}
          totalRows={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingMode="server"
        />
      </Box>

      {addCompanyOpen && (
        <AddCompany
          open={addCompanyOpen}
          onClose={() => setAddCompanyOpen(false)}
          onSuccess={listofComapniesApi}
          disableNavigation
        />
      )}
    </Box>
  );
};

export default CompanyLists;
